/* 9mm Can Drop bundle page. One cart line: the package x can variant (its Simple Bundle carries the Winchester packs and the can(s)).
   Add to cart skips UpCart, adds shipping insurance through SubscriptionInsuranceCore, then goes straight to the Club upsell page. */
(() => {
  if (customElements.get('candrop-page')) return;
  const OFFER_ID = '9mm-can-drop';
  const BOTH = 'Both Cans';
  const SHORT = { 'Shall Not Be Infringed': 'Shall Not Be Infringed', Parabellum: 'Parabellum' };
  const TIERS = { Starter: { packs: 2, rounds: '400' }, 'Range Can': { packs: 3, rounds: '600' }, 'Range Day': { packs: 5, rounds: '1,000', both: true },
    Stockpile: { packs: 10, rounds: '2,000', both: true } };   // Stockpile (owner, Oct 20): 2,000 rounds + both cans
  const shipsBoth = (tier) => Boolean(TIERS[tier]?.both);

  const money = (cents) => {
    const d = Number(cents || 0) / 100;
    return '$' + (Number.isInteger(d) ? d.toFixed(0) : d.toFixed(2));
  };

  customElements.define('candrop-page', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-candrop-variants]')?.textContent || '[]');
      this.images = JSON.parse(this.querySelector('[data-candrop-images]')?.textContent || '{}');
      this.redirectUrl = this.normalizeRedirectUrl(this.dataset.redirectUrl);
      this.state = {
        tier: this.dataset.defaultTier || 'Range Can',
        can: this.dataset.defaultCan || 'Shall Not Be Infringed',
        busy: false,
      };
      this.form = this.querySelector('[data-candrop-buy]');
      // Email deep links: ?pick=Stockpile (or any tier name) opens with that package selected; unknown values are ignored.
      const pick = new URLSearchParams(location.search).get('pick');
      if (pick && TIERS[pick]) {
        this.state.tier = pick;
        const radio = this.form?.querySelector(`input[name="candrop-tier"][value="${pick}"]`);
        if (radio) radio.checked = true;
      }
      this.hero = this.querySelector('[data-candrop-hero]');
      this.sticky = this.querySelector('[data-candrop-sticky]');

      this.renderPrices();
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'candrop-tier') this.state.tier = event.target.value;
        if (event.target.name === 'candrop-can') this.state.can = event.target.value;
        this.update(true);
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.addToCart(); });
      this.querySelector('[data-candrop-sticky-btn]')?.addEventListener('click', () => this.addToCart());
      // The closing button buys the current pick outright instead of scrolling the shopper back up to the form.
      this.querySelector('[data-candrop-close-add]')?.addEventListener('click', () => this.addToCart());
      this.querySelectorAll('[data-candrop-jump]').forEach((link) => link.addEventListener('click', (event) => {
        event.preventDefault();
        this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
      // "Today" quick pick: set the load, then take the shopper to the can choice and the button.
      this.querySelectorAll('[data-candrop-quick]').forEach((btn) => btn.addEventListener('click', () => {
        const radio = this.form.querySelector(`input[name="candrop-tier"][value="${btn.dataset.candropQuick}"]`);
        if (radio) { radio.checked = true; this.state.tier = radio.value; this.update(true); }
        this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
      // The engraving close-ups under the hero pick the can too.
      this.querySelectorAll('button[data-candrop-detail]').forEach((btn) => btn.addEventListener('click', () => {
        if (shipsBoth(this.state.tier)) return;
        const radio = this.form.querySelector(`input[name="candrop-can"][value="${btn.dataset.candropDetail}"]`);
        if (radio) { radio.checked = true; this.state.can = radio.value; this.update(true); }
      }));
      this.observeBuyBox();
      this.observeStoryBar();
      this.update(false);
      if (document.readyState === 'complete') this.preloadHeroes(); else window.addEventListener('load', () => this.preloadHeroes(), { once: true });
    }

    // Starter and Range Can use the picked can; Range Day and Stockpile always ship both.
    currentCan() { return shipsBoth(this.state.tier) ? BOTH : this.state.can; }

    currentVariant() {
      return this.variants.find((v) => v.tier === this.state.tier && v.can === this.currentCan()) || null;
    }

    // Price per package: the selected can's variant (Range Day has one). Strikethrough = the items bought separately.
    renderPrices() {
      Object.keys(TIERS).forEach((tier) => {
        const v = this.variants.find((x) => x.tier === tier && (shipsBoth(tier) ? x.can === BOTH : x.can === this.state.can))
          || this.variants.find((x) => x.tier === tier);
        const el = this.querySelector(`[data-candrop-price="${tier}"]`);
        if (!v || !el) return;
        const saving = v.compare > v.price ? `<s aria-label="Bought separately ${money(v.compare)}">${money(v.compare)}</s><small>if bought separately</small>` : '';
        el.innerHTML = `${money(v.price)}${saving}`;
        const quick = this.querySelector(`[data-candrop-quick-price="${tier}"]`);
        if (quick) quick.textContent = money(v.price);
      });
      const from = Math.min(...this.variants.map((v) => v.price));
      this.querySelectorAll('[data-candrop-from]').forEach((el) => { el.textContent = money(from); });
    }

    update(swapImage) {
      const rangeDay = shipsBoth(this.state.tier);   // both cans: Range Day and Stockpile
      this.querySelector('[data-candrop-cans]').hidden = rangeDay;
      this.querySelector('[data-candrop-both]').hidden = !rangeDay;

      const variant = this.currentVariant();
      const t = TIERS[this.state.tier] || TIERS['Range Can'];
      const canPhrase = rangeDay ? 'both engraved cans' : `the ${SHORT[this.state.can] || this.state.can} can`;
      const cta = this.querySelector('[data-candrop-cta]');
      cta.textContent = !variant ? 'Unavailable' : !variant.available ? 'Sold out' : `Add to cart · ${money(variant.price)}`;
      cta.disabled = !variant || !variant.available || this.state.busy;
      cta.setAttribute('aria-busy', this.state.busy ? 'true' : 'false');
      const closeAdd = this.querySelector('[data-candrop-close-add]');
      if (closeAdd) {
        // Tier and price stay together on one line so a narrow phone never strands the price.
        if (variant && variant.available) closeAdd.innerHTML = `Add to cart<span class="candrop-close-sep"> · </span><span class="candrop-nowrap">${this.state.tier} · ${money(variant.price)}</span>`;
        else closeAdd.textContent = cta.textContent;
        closeAdd.disabled = cta.disabled;
      }
      this.querySelector('[data-candrop-summary]').textContent = `${this.state.tier}: ${t.rounds} rounds + ${canPhrase}.`;
      this.querySelector('[data-candrop-contents]').textContent =
        `${t.packs} × 200-rd Winchester packs · ${t.rounds} rounds · ${rangeDay ? '2 engraved .50 cal cans' : '1 engraved .50 cal can'}`;
      this.renderSticky(variant, t, canPhrase);
      this.querySelectorAll('[data-candrop-quick]').forEach((b) => b.classList.toggle('is-on', b.dataset.candropQuick === this.state.tier));

      const tagR = this.querySelector('[data-candrop-tag-rounds]'); const tagC = this.querySelector('[data-candrop-tag-can]');
      if (tagR) tagR.textContent = variant ? `${t.rounds} rounds · ${money(variant.price)}` : `${t.rounds} rounds`;
      if (tagC) tagC.textContent = rangeDay ? '+ Both engraved cans' : '+ Engraved .50 cal can';
      this.querySelectorAll('[data-candrop-detail]').forEach((el) => {
        el.classList.toggle('is-on', rangeDay || el.dataset.candropDetail === this.state.can);
        el.setAttribute('aria-pressed', String(!rangeDay && el.dataset.candropDetail === this.state.can));
      });
      this.swapHero(this.images[`${this.state.tier}|${this.currentCan()}`], swapImage);
      this.renderPrices();
    }

    // Sticky bar mirrors the pick: thumbnail, tier, what's in it, price. Phones fold the price into the button.
    renderSticky(variant, t, canPhrase) {
      if (!this.sticky) return;
      const ok = variant && variant.available;
      this.sticky.querySelector('[data-candrop-sticky-name]').textContent = this.state.tier;
      this.sticky.querySelector('[data-candrop-sticky-meta]').textContent = `${t.rounds} rounds + ${canPhrase}`;
      this.sticky.querySelector('[data-candrop-sticky-price]').innerHTML = ok
        ? `${money(variant.price)}${variant.compare > variant.price ? `<span class="candrop-sticky-was"><s>${money(variant.compare)}</s> if bought separately</span>` : ''}` : '';
      const btn = this.sticky.querySelector('[data-candrop-sticky-btn]');
      btn.innerHTML = ok ? `Add to cart<span class="candrop-sticky-btn-price"> · ${money(variant.price)}</span>` : (variant ? 'Sold out' : 'Unavailable');
      btn.disabled = !ok || this.state.busy;
      const img = this.images[`${this.state.tier}|${this.currentCan()}`];
      const thumb = this.sticky.querySelector('[data-candrop-sticky-thumb]');
      if (img && thumb && thumb.getAttribute('src') !== img[0]) thumb.src = img[0];
    }

    // Hero swap: decode the next photo off-screen, then switch in one frame. Setting src directly made Firefox (and cold
    // caches anywhere) show a dimmed or half-loaded hero for ~0.3-0.5s on the first pick of each tier/can.
    swapHero(img, animate) {
      if (!img || !this.hero || this.hero.getAttribute('src') === img[0]) return;
      const token = (this.swapToken = (this.swapToken || 0) + 1);
      const next = new Image();
      next.sizes = this.hero.sizes;
      next.srcset = `${img[0]} 800w, ${img[1]} 1600w`;
      next.src = img[0];
      const apply = () => {
        if (token !== this.swapToken) return;   // a newer pick won
        this.hero.srcset = next.srcset;
        this.hero.src = img[0];
        this.hero.classList.remove('is-swapping');
      };
      const slow = animate && setTimeout(() => { if (token === this.swapToken) this.hero.classList.add('is-swapping'); }, 150);
      (next.decode ? next.decode() : Promise.resolve()).catch(() => {}).then(() => { clearTimeout(slow); apply(); });
    }

    // Warm every hero once the page is idle so later picks swap instantly.
    preloadHeroes() {
      const run = () => Object.values(this.images).forEach((img) => {
        const pre = new Image();
        pre.sizes = this.hero?.sizes || '100vw';
        pre.srcset = `${img[0]} 800w, ${img[1]} 1600w`;
        pre.src = img[0];
      });
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 1500);
    }

    // Sticky bar (owner, Oct 17): only after the shopper has scrolled DOWN past the Add to Cart button, so the selections
    // above it are made first. Hidden while the button is on screen or still below, and again at the closing CTA.
    observeBuyBox() {
      if (!this.sticky) return;
      const cta = this.querySelector('[data-candrop-cta]');
      const close = this.querySelector('.candrop-close');
      let queued = false;
      // Read positions on every scroll (rAF-throttled): an IntersectionObserver misses jumps that skip the button entirely.
      const sync = () => {
        queued = false;
        const passedCta = cta.getBoundingClientRect().bottom < 0;
        const closeTop = close ? close.getBoundingClientRect().top : Infinity;
        this.sticky.hidden = !passedCta || closeTop < window.innerHeight;
      };
      const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(sync); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      sync();
    }

    // Story first only: a way into the options on every screen of the story. Shows once the opening has scrolled away,
    // hides as soon as the buy box comes on screen (the Add to Cart sticky above takes over past the button).
    observeStoryBar() {
      const bar = this.querySelector('[data-candrop-story-bar]');
      const intro = this.querySelector('.candrop-intro');
      const buy = this.querySelector('.candrop-hero');
      if (!bar || !intro || !buy) return;
      let queued = false;
      const sync = () => {
        queued = false;
        const pastIntro = intro.getBoundingClientRect().bottom < 0;
        const beforeBuy = buy.getBoundingClientRect().top > window.innerHeight;
        bar.hidden = !(pastIntro && beforeBuy);
      };
      const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(sync); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      sync();
    }

    async addToCart() {
      if (this.state.busy) return;
      const variant = this.currentVariant();
      if (!variant || !variant.available) { this.showError('That package is sold out right now.'); return; }
      this.showError('');
      this.setBusy(true);
      try {
        const items = [{
          id: Number(variant.id), quantity: 1,
          properties: { _pa_offer: OFFER_ID, _pa_variant_sku: variant.sku || '' },
        }];
        const insuranceItem = await this.getInsuranceCartItem(Number(variant.price || 0));
        if (insuranceItem) items.push(insuranceItem);

        const response = await fetch(`${this.getRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(String(error.description || error.message || '') || 'Unable to add this package.');
        }
        window.SubscriptionInsuranceCore?.clearCartCache?.();
        this.track('candrop_add_to_cart', { package: this.state.tier, can: this.currentCan(), variant_id: String(variant.id), variant_sku: variant.sku, value: variant.price / 100 });
        window.location.href = this.redirectUrl;
      } catch (error) {
        this.setBusy(false);
        this.showError(error.message || 'Unable to add this package.');
      }
    }

    getInsuranceCartItemSync() {
      const s = window.theme?.shopSettings || {};
      const id = Number(s.shippingInsuranceVariantId);
      return s.shippingInsuranceEnabled && id ? { id, quantity: 1 } : null;
    }

    // Same call as the other offer pages: tier the protection line on the cart as it will be after this add.
    async getInsuranceCartItem(priceCents) {
      try {
        if (window.SubscriptionInsuranceCore) {
          window.SubscriptionInsuranceCore.clearCartCache?.();
          const action = await window.SubscriptionInsuranceCore.determineInsuranceAction(false, null, { merchandiseSubtotalDelta: Number(priceCents || 0) });
          if (action?.tier === 'premium') return null; // $1,000+ carts: premium tier is opt-in at checkout, never auto-added
          return await window.SubscriptionInsuranceCore.executeInsuranceAction(action);
        }
      } catch (error) {
        return this.getInsuranceCartItemSync();
      }
      return this.getInsuranceCartItemSync();
    }

    setBusy(busy) {
      this.state.busy = busy;
      if (busy) this.suppressUpCart(); else this.releaseUpCart();
      this.update(false);
    }

    // Keep UpCart from opening on our cart/add.js call: the shopper goes straight to the Club page.
    suppressUpCart() {
      if (this.upCartSuppressed) return;
      this.upCartSuppressed = true;
      this.hadSkipFlag = Object.prototype.hasOwnProperty.call(window, '__paSkipUpCart');
      this.previousSkip = window.__paSkipUpCart;
      window.__paSkipUpCart = true;
      document.body.classList.add('candrop-cart-transition');
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.querySelector('details')?.hasAttribute('open')) drawer.dispatchEvent(new CustomEvent('drawer:force-close'));
    }

    releaseUpCart() {
      if (!this.upCartSuppressed) return;
      this.upCartSuppressed = false;
      document.body.classList.remove('candrop-cart-transition');
      if (this.hadSkipFlag) window.__paSkipUpCart = this.previousSkip; else delete window.__paSkipUpCart;
    }

    showError(message) {
      const el = this.querySelector('[data-candrop-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    normalizeRedirectUrl(url) {
      const u = (url || '/checkout').trim();
      return !u || /^\/?cart\/?$/i.test(u) ? '/checkout' : u;
    }

    getRoot() { return window.Shopify?.routes?.root || '/'; }

    track(name, params) {
      const payload = Object.assign({ event_category: 'candrop_9mm', offer_id: OFFER_ID, page_path: window.location.pathname }, params || {});
      try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: name }, payload)); } catch (e) {}
      try { if (typeof window.gtag === 'function') window.gtag('event', name, payload); } catch (e) {}
      try { window.Shopify?.analytics?.publish?.(name, payload); } catch (e) {}
    }
  });
})();
